import React from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Target, Award } from 'lucide-react';
export function Layer4Scorecard() {
  const factors = [{
    factor: 'Average monthly GMV',
    weight: '30%',
    computed: '12mo weighted avg (recent-biased); scaled against tier thresholds'
  }, {
    factor: 'Revenue consistency (CoV)',
    weight: '25%',
    computed: 'CoV < 0.20 = 100pts; CoV > 0.50 = 0pts; linear interpolation'
  }, {
    factor: 'GMV trend (momentum)',
    weight: '20%',
    computed: 'MoM growth last 3mo vs prior 3mo; positive trajectory rewarded'
  }, {
    factor: 'Customer diversity',
    weight: '15%',
    computed: 'HHI concentration index; low concentration = high score'
  }, {
    factor: 'PSP tenure',
    weight: '10%',
    computed: 'Months as active PSP client; capped at 24 months for max score'
  }, {
    factor: 'Operational quality',
    weight: '10%',
    computed: 'CB rate, refund rate, void rate — low rates score high'
  }];
  const bands = [{
    band: 'Platinum',
    range: '85 – 100',
    tier: 'A',
    multiplier: 'Up to 1.5×',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200'
  }, {
    band: 'Gold',
    range: '70 – 84',
    tier: 'B',
    multiplier: 'Up to 1.0×',
    color: 'bg-amber-100 text-amber-800 border-amber-200'
  }, {
    band: 'Silver',
    range: '55 – 69',
    tier: 'C',
    multiplier: 'Up to 0.6×',
    color: 'bg-slate-200 text-slate-800 border-slate-300'
  }, {
    band: 'Bronze',
    range: '40 – 54',
    tier: 'D',
    multiplier: 'Up to 0.35×',
    color: 'bg-orange-100 text-orange-800 border-orange-200'
  }, {
    band: 'Decline',
    range: '< 40',
    tier: '—',
    multiplier: 'No offer',
    color: 'bg-red-100 text-red-800 border-red-200'
  }];
  const example = [{
    factor: 'Avg monthly GMV',
    weight: '30%',
    raw: 'SGD 45k',
    score: '72',
    pts: '21.6'
  }, {
    factor: 'Revenue consistency (CoV)',
    weight: '25%',
    raw: '0.18',
    score: '82',
    pts: '20.5'
  }, {
    factor: 'GMV trend',
    weight: '20%',
    raw: '+8% MoM',
    score: '75',
    pts: '15.0'
  }, {
    factor: 'Customer diversity',
    weight: '15%',
    raw: 'Moderate HHI',
    score: '60',
    pts: '9.0'
  }, {
    factor: 'PSP tenure',
    weight: '10%',
    raw: '14 months',
    score: '68',
    pts: '6.8'
  }, {
    factor: 'Operational quality',
    weight: '10%',
    raw: '0.3% CB rate',
    score: '88',
    pts: '8.8'
  }];
  return <SectionWrapper title="Layer 4: Predictive Scorecard" subtitle="Merchants who pass all hard gates are scored using a weighted, factor-based scorecard. Scores range from 0 to 100 and determine both eligibility for a loan offer and the loan tier." data-id="element-287">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-288">
        {/* Factors Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-289">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-290">
            <Target className="w-5 h-5 text-indigo-600" data-id="element-291" />
            <h3 className="font-semibold text-slate-900" data-id="element-292">
              Scorecard Factors & Weights
            </h3>
          </div>
          <table className="w-full text-sm text-left" data-id="element-293">
            <thead className="bg-slate-50/50 text-slate-500 font-medium border-b border-slate-100" data-id="element-294">
              <tr data-id="element-295">
                <th className="px-4 py-3" data-id="element-296">Factor</th>
                <th className="px-4 py-3" data-id="element-297">Weight</th>
                <th className="px-4 py-3" data-id="element-298">How it is computed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-299">
              {factors.map((f, i) => <tr key={i} data-id="element-300">
                  <td className="px-4 py-3 font-medium text-slate-900" data-id="element-301">
                    {f.factor}
                  </td>
                  <td className="px-4 py-3 font-mono text-indigo-600" data-id="element-302">
                    {f.weight}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs" data-id="element-303">
                    {f.computed}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Score Bands */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-304">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-305">
            <Award className="w-5 h-5 text-amber-500" data-id="element-306" />
            <h3 className="font-semibold text-slate-900" data-id="element-307">
              Score Bands & Loan Tiers
            </h3>
          </div>
          <div className="p-4 space-y-3" data-id="element-308">
            {bands.map((b, i) => <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${b.color}`} data-id="element-309">
                <div className="flex items-center gap-4" data-id="element-310">
                  <span className="font-bold w-20" data-id="element-311">{b.band}</span>
                  <span className="font-mono text-sm opacity-80" data-id="element-312">
                    {b.range}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-313">
                  <span className="font-bold" data-id="element-314">Tier {b.tier}</span>
                  <span className="text-sm font-medium w-24" data-id="element-315">
                    {b.multiplier}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Worked Example */}
      <div className="bg-slate-900 rounded-xl shadow-lg overflow-hidden text-slate-300" data-id="element-316">
        <div className="p-6 md:p-8 border-b border-slate-800" data-id="element-317">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-318">
            <div data-id="element-319">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-320">
                Worked Example: Gold Tier Merchant
              </h3>
              <p className="text-sm text-slate-400 max-w-2xl" data-id="element-321">
                Merchant profile: F&B restaurant, 14 months on PSP, SGD 45,000
                average monthly GMV, CoV of 0.18, +8% MoM trend, moderate
                customer diversity, 0.3% chargeback rate.
              </p>
            </div>
            <div className="flex-shrink-0 flex flex-col items-center justify-center bg-slate-800 rounded-full w-32 h-32 border-4 border-amber-500 shadow-[0_0_15px_rgba(245,158,11,0.3)]" data-id="element-322">
              <span className="text-3xl font-bold text-white" data-id="element-323">81.7</span>
              <span className="text-xs text-amber-400 font-medium uppercase tracking-wider mt-1" data-id="element-324">
                Gold Tier
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-325">
          <table className="w-full text-sm text-left" data-id="element-326">
            <thead className="bg-slate-800/50 text-slate-400 font-medium border-b border-slate-800" data-id="element-327">
              <tr data-id="element-328">
                <th className="px-6 py-4" data-id="element-329">Factor</th>
                <th className="px-6 py-4" data-id="element-330">Weight</th>
                <th className="px-6 py-4" data-id="element-331">Raw Value</th>
                <th className="px-6 py-4" data-id="element-332">Score (0-100)</th>
                <th className="px-6 py-4 text-right text-white" data-id="element-333">
                  Weighted Pts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50" data-id="element-334">
              {example.map((row, i) => <tr key={i} className="hover:bg-slate-800/30 transition-colors" data-id="element-335">
                  <td className="px-6 py-4 font-medium text-slate-200" data-id="element-336">
                    {row.factor}
                  </td>
                  <td className="px-6 py-4 font-mono text-indigo-400" data-id="element-337">
                    {row.weight}
                  </td>
                  <td className="px-6 py-4 text-slate-400" data-id="element-338">{row.raw}</td>
                  <td className="px-6 py-4 font-mono text-slate-300" data-id="element-339">
                    {row.score}
                  </td>
                  <td className="px-6 py-4 font-mono text-right text-amber-400 font-bold" data-id="element-340">
                    {row.pts}
                  </td>
                </tr>)}
              <tr className="bg-slate-800/50" data-id="element-341">
                <td colSpan={4} className="px-6 py-4 font-bold text-white text-right" data-id="element-342">
                  TOTAL SCORE
                </td>
                <td className="px-6 py-4 font-mono text-right text-white font-bold text-lg" data-id="element-343">
                  81.7 / 100
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="p-6 bg-amber-500/10 border-t border-amber-500/20 text-amber-200 text-sm" data-id="element-344">
          <strong data-id="element-345">Result:</strong> Score of 81.7 → Gold (Tier B) → Maximum loan
          ceiling of SGD 45,000 × 1.0 = <strong data-id="element-346">SGD 45,000</strong>
        </div>
      </div>
    </SectionWrapper>;
}